import { DefaultButton, Panel, SpinButton } from "@fluentui/react";
import { useEffect, useRef, useState } from "react";
// import { SparkleFilled } from "@fluentui/react-icons";
import logo from "../../assets/logo-noglow.png";
import forbidden_logo from "../../assets/alexandria_inspector_1.png";
import readNDJSONStream from "ndjson-readablestream";

import styles from "./RiskAnalysis.module.css";

import {
    riskAnalysisApiV1,
    ChatAppResponse,
    ChatAppResponseOrError,
    configApiV1,
    ResponseMessage,
    ApiError,
    loadPowerplantConfigV2,
    RiskAnalysisAppRequest,
    VectorFieldOptions,
    PowerplantInfo
} from "../../api";
import { useGetToken, useIsLoggedIn } from "../../authConfig";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { ClearChatButton } from "../../components/ClearChatButton";
import { FilterSettings } from "../../components/FilterSettings";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { Spinner } from "../../components/Spinner";
import { usePowerPlant } from "../../components/powerplant";

const RiskAnalysis = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [filters, setFilters] = useState<{ name: string; displayName: string; values: { text: string; key: string }[] }[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Both]);

    const [filtersLoading, setFiltersLoading] = useState(false);
    // Auth error handlers during getting config
    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState<null | string>(null);
    const [configLoaded, setConfigLoaded] = useState(false);
    const correlationId = useRef<string | null>(null);
    const [currentPlantImageUrl, setCurrentPlantImageUrl] = useState<string | undefined>(undefined);
    const [imageLoading, setImageLoading] = useState<boolean>(false);
    const [imageError, setImageError] = useState<boolean>(false);

    // Use context instead of local state for powerplants
    const previousPowerplantRef = useRef<string | null>(null);
    const { powerplants, setPowerplants, selectedPlantId, setSelectedPlantId, setSelectedPlantDetails } = usePowerPlant();

    const getConfig = async () => {
        console.log("Fetching configuration...");

        setConfigLoading(true);
        setConfigError(null);

        try {
            const config = await configApiV1(token);
            console.log(config);
            // Handle both old format (string) and new format (object)
            const normalizedPowerplants: Record<string, PowerplantInfo> = {};
            Object.entries(config.powerplants ?? {}).forEach(([key, value]) => {
                if (typeof value === "string") {
                    normalizedPowerplants[key] = { id: value };
                } else {
                    normalizedPowerplants[key] = value as PowerplantInfo;
                }
            });

            setPowerplants(normalizedPowerplants);

            if (Object.keys(normalizedPowerplants).length > 0) {
                const firstNonSpecial = Object.keys(normalizedPowerplants).find(k => k !== "demo" && k !== "lorin");
                const selectedId = firstNonSpecial || Object.keys(normalizedPowerplants)[0];
                setSelectedPlantId(selectedId);

                // Set initial image
                const imageUrl = normalizedPowerplants[selectedId]?.imageUrl;
                if (imageUrl) {
                    setImageLoading(true);
                    setCurrentPlantImageUrl(imageUrl);
                }
            }
            setConfigLoaded(true);
        } catch (err: any) {
            console.log(err);
            if (err instanceof ApiError) {
                console.error(`Config API error ${err.status}: ${err.body}`);
                if (err.status === 403) {
                    setConfigError("You are not authorized to use this service. Please contact alexandria.gen@engie.com.");
                } else {
                    console.log(err);
                    setConfigError("Failed to load configuration.");
                }
            } else {
                console.error("Unexpected error:", err);
            }
        } finally {
            setConfigLoading(false);
        }
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;
        console.log("Handling async request for question:", question, "with answers:", answers);

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    console.log("Updating state with new content:", newContent);
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
                    };
                    setStreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                console.log(event);
                if (event["choices"] && event["choices"][0]["context"] && event["choices"][0]["context"]["data_points"]) {
                    event["choices"][0]["message"] = event["choices"][0]["delta"];
                    console.log(event);
                    askResponse = event as ChatAppResponse;
                } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["choices"][0]["delta"]["content"]);
                } else if (event["choices"] && event["choices"][0]["context"]) {
                    // Update context with new keys from latest event
                    askResponse.choices[0].context = { ...askResponse.choices[0].context, ...event["choices"][0]["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
        };
        return fullResponse;
    };

    const token = useGetToken();
    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            console.log("Making API request with question:", question);
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].choices[0].message.content, role: "assistant" }
            ]);
            const request: RiskAnalysisAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                stream: shouldStream,
                context: {
                    callerApp: `alexandria_ui`,
                    site: selectedPlantId ?? undefined,
                    overrides: {
                        top: retrieveCount,
                        filter_powerplant: !selectedPlantId || selectedPlantId.length === 0 ? undefined : "riskanalysis",
                        filters: selectedFilters,
                        vector_fields: vectorFieldList
                    }
                },
                // ChatAppProtocol: Client must pass on any session state received from the server
                session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
            };
            console.log("API request:", request);

            const response = await riskAnalysisApiV1(request, token, correlationId.current);
            console.log(response);
            correlationId.current = response.headers.get("X-Correlation-ID");
            if (!response.body) {
                throw Error("No response body");
            }
            if (shouldStream) {
                console.log("Streaming response for answers:", answers);
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answers, response.body);
                setAnswers([...answers, [question, parsedResponse]]);
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    throw Error(parsedResponse.error || "Unknown error");
                }
                setAnswers([...answers, [question, parsedResponse as ChatAppResponse]]);
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        correlationId.current = null;
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const notLoggedIn = !useIsLoggedIn();

    const onSettingsClick = () => {
        setIsConfigPanelOpen(prev => !prev);
    };

    useEffect(() => {
        if (selectedPlantId && selectedPlantId !== previousPowerplantRef.current) {
            const loadPowerplantUI = async () => {
                try {
                    setFiltersLoading(true);
                    // Update plant image immediately
                    const plantInfo = powerplants[selectedPlantId];
                    if (plantInfo?.imageUrl) {
                        setImageLoading(true);
                        setImageError(false);
                        setCurrentPlantImageUrl(plantInfo.imageUrl);
                    } else {
                        setCurrentPlantImageUrl(undefined);
                        setImageLoading(false);
                    }
                    // Fetch powerplant config from the backend
                    const powerplantConfig = await loadPowerplantConfigV2(selectedPlantId, token);
                    console.log("Loaded powerplant config:", powerplantConfig);
                    // Update powerplant details if callback provided
                    if (setSelectedPlantDetails) {
                        setSelectedPlantDetails({
                            id: powerplantConfig.id,
                            name: "",
                            imageUrl: plantInfo?.imageUrl,
                            sharepointLibraryUrl: powerplantConfig.sharepointLibraryUrl || "",
                            exampleQuestions: powerplantConfig.exampleQuestions || []
                        });
                    }
                    setFilters([]); // Reset filters immediately
                    setSelectedFilters({}); // Reset filter values immediately

                    previousPowerplantRef.current = selectedPlantId;
                } catch (error) {
                    console.error("Error fetching powerplant configuration:", error);
                } finally {
                    setFiltersLoading(false);
                }
            };
            loadPowerplantUI();
        }
    }, [selectedPlantId]);

    // Add image load handlers
    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageLoading(false);
        setImageError(true);
        setCurrentPlantImageUrl(undefined);
    };

    return (
        <div className={styles.container}>
            <div className={styles.commandsContainer}>
                <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
            </div>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            {/* Logo */}
                            <div style={{ transform: "scale(3.5)", paddingTop: "60px", paddingBottom: "50px" }}>
                                {configError ? (
                                    <img src={forbidden_logo} alt="Chat logo" aria-hidden="true" style={{ width: "44px", height: "44px" }} />
                                ) : (
                                    <img src={logo} alt="Chat logo" aria-hidden="true" style={{ width: "44px", height: "44px" }} />
                                )}
                            </div>
                            <h1 className={styles.chatEmptyStateTitle}>Risk Analysis with AlexandrIA</h1>
                            <h2 className={styles.chatEmptyStateSubtitle}>
                                Please provide the work description, location, duration, tools and materials needed, certifications of the technicians.
                            </h2>
                            {configLoading ? (
                                <Spinner label="Loading configuration..." />
                            ) : configError ? (
                                <div style={{ color: "red", padding: "20px", fontWeight: "bold" }}>{configError}</div>
                            ) : (
                                <div></div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={streamedAnswer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                isSelected={false}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {!isStreaming &&
                                answers.map((answer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={answer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={false}
                                                key={index}
                                                answer={answer[1]}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {isLoading && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}
                    <div className={styles.chatInput}>
                        {configLoading ? null : configError ? null : (
                            <QuestionInput
                                clearOnSend
                                placeholder="Type a new question"
                                disabled={isLoading}
                                onSend={question => makeApiRequest(question)}
                                onFilterToggle={onSettingsClick}
                                hasActiveFilters={Object.values(selectedFilters).some(v => Array.isArray(v) && v.length > 0)}
                            />
                        )}
                    </div>
                </div>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}

                <Panel
                    headerText="Configure answer generation"
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel="Close"
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label="Retrieve this many search results:"
                        min={1}
                        max={50}
                        defaultValue={retrieveCount.toString()}
                        onChange={onRetrieveCountChange}
                    />
                    {filtersLoading ? (
                        <Spinner label="Loading filters..." />
                    ) : (
                        <FilterSettings filters={filters} selectedFilters={selectedFilters} updateFilters={setSelectedFilters} />
                    )}
                </Panel>
            </div>
        </div>
    );
};

export default RiskAnalysis;
